import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { connect } from "react-redux";
import { Formik, Field, Form } from "formik";
import { Row, Col, FormGroup, Label, Button } from "reactstrap";
import SimpleReactValidator from "simple-react-validator";
import logoLight from "../../../assets/images/logo-light.webp";
import { __t } from "../../../i18n/translator";
import { getVendorLoginRequest } from "../../../redux/vendor/auth/login/action";
import { vendorSessionExpiredRequest } from "../../../redux/vendor/auth/logout/action";
import VendorAuthChips from "./VendorAuthChips";
import {
    isVendorLoggedIn,
    removeLoggedInVendorDetails,
} from "../../../utils/authUtils";
import {
    UserOutlineSvg,
    LockSvg,
    EyeOutlineSvg,
    EyeOffOutlineSvg,
    VendorWaveTopSvg,
    VendorWaveBottomSvg,
    VendorDonutSvg,
} from "../../../components/CommonSvg";

const VendorLogin = (props) => {
    const { loginVendorData, loginData, sessionExpired } = props;
    const navigate = useNavigate();
    const validator = useRef(new SimpleReactValidator());
    const [showPassword, setShowPassword] = useState(false);

    
    const rememberedEmail = localStorage.getItem("rememberedVendorEmail");
    const rememberedPassword = localStorage.getItem("rememberedVendorPassword");

    const initialValues = {
        email: rememberedEmail || "",
        password: rememberedPassword || "",
        rememberMe: Boolean(rememberedEmail),
    };

    const handleSubmit = (values) => {
        if (validator.current.allValid()) {
            if (values.rememberMe) {
                localStorage.setItem("rememberedVendorEmail", values.email.trim());
                localStorage.setItem("rememberedVendorPassword", values.password);
            } else {
                localStorage.removeItem("rememberedVendorEmail");
                localStorage.removeItem("rememberedVendorPassword");
            }
            loginVendorData({
                email: values.email.trim(),
                password: values.password,
            });
        } else {
            validator.current.showMessages();
        }
    };
    
    useEffect(() => {
        if (isVendorLoggedIn()) {
            navigate("/vendor/dashboard", { replace: true });
        } else if (localStorage.getItem("vendorAccessToken")) {
            sessionExpired();
        } else {
            removeLoggedInVendorDetails();
        }
    }, []);

    useEffect(() => {
        if (loginData?.status === true && localStorage.getItem("vendorAccessToken")) {
            navigate("/vendor/dashboard");
        }
    }, [loginData?.status]);

    return (
        <div className="vendor-auth">
            <div className="va-wrap">
                <Row className="va-card g-0 mx-0">
                    
                    <Col lg="6" className="va-brand">
                        <VendorWaveTopSvg />
                        <div className="va-glow" />

                        <img className="va-logo" src={logoLight} alt="HRA" />

                        <div className="va-laptop">
                            <div className="va-screen">
                                <div className="va-dash">
                                    <div className="va-side">
                                        <i /><i /><i /><i /><i />
                                    </div>
                                    <div className="va-panel">
                                        <div className="va-t" />
                                        <div className="va-bars">
                                            <b /><b /><b /><b /><b />
                                        </div>
                                    </div>
                                    <div className="va-panel">
                                        <div className="va-t" />
                                        <VendorDonutSvg />
                                        <div className="va-rows">
                                            <i /><i /><i />
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="va-base" />
                        </div>

                        <VendorAuthChips />

                        <VendorWaveBottomSvg />
                    </Col>

                    
                    <Col lg="6" className="va-form-side">
                        <h1 className="va-title">Vendor Portal</h1>
                        <p className="va-sub">Welcome back! Please enter your details below.</p>

                        <Formik initialValues={initialValues} onSubmit={handleSubmit}>
                            {({ values }) => {
                                return (
                                    <Form role={"form"} className="form-login">
                                        <FormGroup className="va-field">
                                            <Label className="va-label" htmlFor="email">
                                                Email Address <span className="va-required">*</span>
                                            </Label>
                                            <div className="va-control">
                                                <span className="va-ic">
                                                    <UserOutlineSvg />
                                                </span>
                                                <Field
                                                    id="email"
                                                    name="email"
                                                    type="email"
                                                    className="va-input"
                                                    placeholder="Enter your email address"
                                                    autoComplete="username"
                                                    maxLength={99}
                                                />
                                            </div>
                                            {validator.current.message(
                                                __t("validations.auth.email"),
                                                values.email,
                                                "required|email",
                                                {
                                                    messages: {
                                                        required: __t("validations.auth.email"),
                                                        email: __t("validations.auth.email_valid"),
                                                    },
                                                }
                                            )}
                                        </FormGroup>

                                        <FormGroup className="va-field">
                                            <Label className="va-label" htmlFor="password">
                                                Password <span className="va-required">*</span>
                                            </Label>
                                            <div className="va-control">
                                                <span className="va-ic">
                                                    <LockSvg />
                                                </span>
                                                <Field
                                                    id="password"
                                                    name="password"
                                                    type={showPassword ? "text" : "password"}
                                                    className="va-input"
                                                    placeholder="Enter your password"
                                                    autoComplete="current-password"
                                                    maxLength={99}
                                                    minLength={6}
                                                />
                                                <button
                                                    type="button"
                                                    className="va-eye"
                                                    aria-label={showPassword ? "Hide password" : "Show password"}
                                                    onClick={() => setShowPassword((prev) => !prev)}
                                                >
                                                    {showPassword ? <EyeOffOutlineSvg /> : <EyeOutlineSvg />}
                                                </button>
                                            </div>
                                            {validator.current.message(
                                                __t("validations.auth.password"),
                                                values.password,
                                                "required|min:6",
                                                {
                                                    messages: {
                                                        required: __t("validations.auth.password"),
                                                        min: __t("validations.auth.min_password"),
                                                    },
                                                }
                                            )}
                                        </FormGroup>

                                        <div className="va-row">
                                            <Label className="va-remember" htmlFor="rememberMe">
                                                <Field
                                                    type="checkbox"
                                                    id="rememberMe"
                                                    name="rememberMe"
                                                    checked={values.rememberMe}
                                                />
                                                Remember me
                                            </Label>
                                            <Link className="va-forgot" to="/vendor/forgot-password">
                                                Forgot password?
                                            </Link>
                                        </div>

                                        <Button type="submit" className="va-btn">
                                            Log in
                                        </Button>
                                    </Form>
                                );
                            }}
                        </Formik>
                    </Col>
                </Row>
            </div>

            <div className="va-footer">
                <a href="https://www.hraga.com/terms-of-service" target="_blank" rel="noreferrer" title="Terms of Service">
                    Terms of Service
                </a>
                •
                <a href="https://www.hraga.com/mbr-app-privacy-policy" target="_blank" rel="noreferrer" title="Privacy Policy">
                    Privacy Policy
                </a>
                •
                <span className="va-copy">© {new Date().getFullYear()} HRA. All rights reserved.</span>
            </div>
        </div>
    );
};

const mapStateToProps = ({ VendorLogin }) => {
    let loginData = null;
    if (VendorLogin?.loginUser?.status) {
        loginData = VendorLogin.loginUser;
    }
    return { loginData: loginData };
};

const mapDispatchToProps = (dispatch) => {
    return {
        loginVendorData: (cred) => dispatch(getVendorLoginRequest(cred)),
        sessionExpired: () => dispatch(vendorSessionExpiredRequest()),
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(VendorLogin);
